<template>
  <div>

    <!-- MOBILE TOP BAR -->
    <div v-if="isMobile" class="d-flex align-items-center p-2 bg-light shadow-sm sticky-top">
      <b-button variant="link" class="p-0 mr-2" @click="drawerOpen = true">
        <b-icon-list></b-icon-list>
      </b-button>
      <span class="font-weight-bold">{{ activeGroupTitle }} · {{ activeTabTitle }}</span>
    </div>

    <!-- MOBILE DRAWER: group switcher + tabs of active group -->
    <b-sidebar
      id="admintools-drawer"
      :title="activeGroupTitle + ' Menu'"
      bg-variant="light"
      text-variant="dark"
      shadow
      backdrop
      placement="left"
      :visible="drawerOpen"
      @hidden="drawerOpen = false"
    >
      <hr/>
      <!-- Group switcher -->
      <div class="px-3 py-3">
        <b-button-group vertical class="w-100">
          <b-button
            v-for="group in availableGroups"
            :key="group.key"
            block
            squared
            :variant="activeGroup === group.key ? 'primary' : 'outline-secondary'"
            class="text-left mb-2 py-2 px-3"
            @click="selectGroup(group.key)"
          >
            {{ group.title }}
          </b-button>
        </b-button-group>
      </div>

      <hr/>

      <!-- Tabs within the active group -->
      <ul class="list-unstyled m-0 px-2">
        <li v-for="(tab, index) in filteredTabs" :key="tab.slug" class="mb-2">
          <b-button
            block
            squared
            :variant="activeTabIndex === index ? null : 'link'"
            class="text-left"
            @click="selectTab(index)"
          >
            <b-icon :icon="tab.icon" class="mr-2"></b-icon>
            {{ tab.title }}
          </b-button>
        </li>
      </ul>

      <hr/>

      <!-- Standalone page link -->
      <div class="px-3 pb-3">
        <b-button
          block
          squared
          variant="outline-primary"
          :to="{ path: '/admin_tools/completed_orders/' }"
          @click="drawerOpen = false"
        >
          <b-icon icon="check2-circle" class="mr-2"></b-icon>
          Completed Orders
        </b-button>
      </div>
    </b-sidebar>

    <!-- DESKTOP: group tabs + inner tabs -->
    <div v-if="!isMobile">
      <!-- Top-level group selector (padded) -->
      <div class="group-tabs-wrapper px-3 pt-3 pb-2">
        <b-tabs
          pills
          class="mb-0"
          :value="activeGroupIndex"
          @input="onGroupTabInput"
        >
          <b-tab
            v-for="group in availableGroups"
            :key="group.key"
            :title="group.title"
          ></b-tab>
        </b-tabs>
      </div>

      <!-- Inner tabs for the active group -->
      <div class="px-3">
        <b-tabs card lazy v-model="activeTabIndex">
          <b-tab
            v-for="(tab, index) in filteredTabs"
            :key="tab.slug"
            :title="tab.title"
          >
            <keep-alive>
              <component :is="tab.component" :key="tab.slug"></component>
            </keep-alive>
          </b-tab>
        </b-tabs>
    
      </div>
    </div>

    <!-- MOBILE: render active component -->
    <div v-if="isMobile" class="mt-2">
      <keep-alive>
        <component
          v-if="activeTabComponent"
          :is="activeTabComponent"
          :key="activeTabComponentKey"
        ></component>
      </keep-alive>
    </div>

  </div>
</template>

<script>
import {
  BIconList, BIconClipboard, BIconArchive, BIconBox, BIconTruck,
  BIconFileText, BIconExclamationTriangle, BIconCalendar
} from "bootstrap-vue";

import AdminToolsAllOrders2 from "@/components/AdminTools/AdminToolsAllOrders2";
import AdminToolsProcessDeliveries from "@/components/AdminTools/AdminToolsProcessDeliveries";
import AdminToolsProcessPickups from "@/components/AdminTools/AdminToolsProcessPickups";
import AdminToolsStoreTransfers2 from "@/components/AdminTools/AdminToolsStoreTransfers2";
import AdminToolsDiscrepancies from "@/components/AdminTools/AdminToolsDiscrepancies";
import AdminToolsTrackInvoice from "@/components/AdminTools/AdminToolsTrackInvoice";
import AdminToolsDeliveredOrders from "@/components/AdminTools/AdminToolsDeliveredOrders";
import AdminToolsRefundRequests from "@/components/AdminTools/AdminToolsRefundRequests";
import AdminToolsItemHistory from "@/components/AdminTools/AdminToolsItemHistory";
import AdminToolsDisputedOrders from "@/components/AdminTools/DisputedOrders/AdminToolsDisputedOrders";
import AdminToolsDeliveryCalendar from "@/components/AdminTools/AdminToolsDeliveryCalendar";
import AdminToolsDeliveryOperations from "@/components/AdminTools/AdminToolsDeliveryOperations";
import AdminToolsDiscrepancyInvoices from "@/components/AdminTools/AdminToolsDiscrepancyInvoices";
import AdminToolsScheduleOrders from "@/components/AdminTools/AdminToolsScheduleOrders";
import AdminToolsRescheduledOrders from "@/components/AdminTools/AdminToolsRescheduledOrders";
import AdminToolsBackorderOrders from "@/components/AdminTools/AdminToolsBackorderOrders";
import AdminToolsPendingOrders from "@/components/AdminTools/AdminToolsPendingOrders";
import AdminToolsCancelledReturnedOrders from "@/components/AdminTools/AdminToolsCancelledReturnedOrders";
import AdminToolsCompletedOrdersPage from "@/components/AdminTools/AdminToolsCompletedOrdersPage";

export default {
  name: "AdminToolsMain",

  components: {
    AdminToolsAllOrders2,
    AdminToolsProcessDeliveries,
    AdminToolsProcessPickups,
    AdminToolsStoreTransfers2,
    AdminToolsDiscrepancies,
    AdminToolsTrackInvoice,
    AdminToolsDeliveredOrders,
    AdminToolsRefundRequests,
    AdminToolsItemHistory,
    AdminToolsDisputedOrders,
    AdminToolsDeliveryCalendar,
    AdminToolsDeliveryOperations,
    AdminToolsDiscrepancyInvoices,
    AdminToolsScheduleOrders,
    AdminToolsRescheduledOrders,
    AdminToolsBackorderOrders,
    AdminToolsPendingOrders,
    AdminToolsCancelledReturnedOrders,
    AdminToolsCompletedOrdersPage,
    BIconList,
    BIconClipboard,
    BIconArchive,
    BIconBox,
    BIconTruck,
    BIconFileText,
    BIconExclamationTriangle,
    BIconCalendar
  },

  data() {
    return {
      drawerOpen: false,
      activeTabIndex: 0,
      activeGroup: "process-sales",
      isMobile: false,
      packet: { userDetails: {} },
      // Prevent watcher feedback loop when syncing from URL
      syncingFromUrl: false,
      // Remember last tab slug per group (per session, in-memory)
      lastTabByGroup: {},

      groups: [
        { key: "process-sales",   title: "PROCESS SALES" },
        { key: "delivered-orders", title: "DELIVERED ORDERS" }
      ],

      tabs: [
        // ---------- PROCESS SALES ----------
        {
          title: "NEW INVOICES",
          icon: "file-text",
          group: "process-sales",
          roles: ["admin","accountmanager","officeuser","officemanager","accountuser"],
          component: "AdminToolsAllOrders2",
          slug: "new-invoices"
        },
        {
          title: "PENDING ORDERS",
          icon: "file-text",
          group: "process-sales",
          roles: ["admin","accountmanager","officeuser","officemanager","accountuser"],
          component: "AdminToolsPendingOrders",
          slug: "pending-orders"
        },
        {
          title: "NOT SCHEDULED ORDERS",
          icon: "file-text",
          group: "process-sales",
          roles: ["admin","accountmanager","officeuser","officemanager","accountuser"],
          component: "AdminToolsBackorderOrders",
          slug: "not-scheduled-orders"
        },
        {
          title: "SCHEDULED ORDERS",
          icon: "file-text",
          group: "process-sales",
          roles: ["admin","accountmanager","officeuser","officemanager","accountuser"],
          component: "AdminToolsScheduleOrders",
          slug: "scheduled-orders"
        },
        {
          title: "RESCHEDULED ORDERS",
          icon: "file-text",
          group: "process-sales",
          roles: ["admin","accountmanager","officeuser","officemanager","accountuser"],
          component: "AdminToolsRescheduledOrders",
          slug: "rescheduled-orders"
        },
        {
          title: "CANCELLED / RETURNED",
          icon: "exclamation-triangle",
          group: "process-sales",
          roles: ["admin","accountmanager","officeuser","officemanager","accountuser"],
          component: "AdminToolsCancelledReturnedOrders",
          slug: "cancelled-returned-orders"
        },

        // ---------- DELIVERED ORDERS ----------
        {
          title: "DELIVERED ORDERS",
          icon: "box",
          group: "delivered-orders",
          roles: ["admin","accountmanager","accountuser","officemanager","officeuser"],
          component: "AdminToolsDeliveredOrders",
          slug: "delivered-orders"
        },
          {
          title: "COMPLETED ORDERS",
          icon: "box",
          group: "delivered-orders",
          roles: ["admin","accountmanager","accountuser","officemanager","officeuser"],
          component: "AdminToolsCompletedOrdersPage",
          slug: "completed-orders"
        },
        {
          title: "CHARGE BACK INVOICES",
          icon: "exclamation-triangle",
          group: "delivered-orders",
          roles: ["admin", "accountmanager", "officeuser", "officemanager"],
          component: "AdminToolsDisputedOrders",
          slug: "charge-back-invoices"
        },
        {
          title: "PAYMENT DISCREPANCIES",
          icon: "exclamation-triangle",
          group: "delivered-orders",
          roles: ["admin","accountmanager","officeuser","officemanager","accountuser"],
          component: "AdminToolsDiscrepancyInvoices",
          slug: "payment-discrepancies"
        }
        
      ]
    };
  },

  computed: {
    // Groups the current user has at least one tab for
    availableGroups() {
      const role = this.packet.userDetails.userrole || "";
      return this.groups.filter(g =>
        this.tabs.some(t => t.group === g.key && t.roles.includes(role))
      );
    },

    activeGroupIndex() {
      return this.availableGroups.findIndex(g => g.key === this.activeGroup);
    },

    activeGroupTitle() {
      const g = this.availableGroups.find(g => g.key === this.activeGroup);
      return g ? g.title : "Menu";
    },

    // Tabs filtered by BOTH role and current group
    filteredTabs() {
      const role = this.packet.userDetails.userrole || "";
      return this.tabs.filter(
        t => t.roles.includes(role) && t.group === this.activeGroup
      );
    },

    activeTabTitle() {
      const tab = this.filteredTabs[this.activeTabIndex];
      return tab ? tab.title : "Menu";
    },

    activeTabComponent() {
      const tab = this.filteredTabs[this.activeTabIndex];
      return tab ? tab.component : null;
    },

    activeTabComponentKey() {
      const tab = this.filteredTabs[this.activeTabIndex];
      return tab ? tab.slug : null;
    }
  },

  watch: {
    // User changes tab within the active group → push URL
    activeTabIndex(newIndex, oldIndex) {
      if (this.syncingFromUrl) return;
      if (newIndex === oldIndex) return;

      const tab = this.filteredTabs[newIndex];
      if (!tab) return;

      // Remember last tab for this group
      this.lastTabByGroup = {
        ...this.lastTabByGroup,
        [this.activeGroup]: tab.slug
      };

      if (
        this.$route.query.tab === tab.slug &&
        this.$route.query.group === this.activeGroup
      ) return;

      this.$router
        .push({
          path: "/admin_tools/main/",
          query: { group: this.activeGroup, tab: tab.slug }
        })
        .catch(err => {
          if (err && err.name !== "NavigationDuplicated") {
            console.error("Tab navigation error:", err);
          }
        });
    },

    // URL group change → switch group, then restore last tab in that group
    "$route.query.group": {
      immediate: true,
      handler(newGroup) {
        if (this.syncingFromUrl) return;

        this.syncingFromUrl = true;

        const valid = this.availableGroups.some(g => g.key === newGroup);
        const nextGroup = valid
          ? newGroup
          : (this.availableGroups[0].key || "process-sales");

        const groupChanged = nextGroup !== this.activeGroup;
        this.activeGroup = nextGroup;

        // Only reset/restore the tab index if the group actually changed.
        // The "$route.query.tab" watcher handles the tab when both change.
        if (groupChanged) {
          const rememberedSlug = this.lastTabByGroup[nextGroup];
          const rememberedIdx = rememberedSlug
            ? this.filteredTabs.findIndex(t => t.slug === rememberedSlug)
            : -1;
          this.activeTabIndex = rememberedIdx !== -1 ? rememberedIdx : 0;
        }

        this.$nextTick(() => { this.syncingFromUrl = false; });
      }
    },

    // URL tab change → set tab index
    "$route.query.tab": {
      immediate: true,
      handler(newSlug) {
        // Guard: when both group & tab change at once, let the group
        // watcher finish first, then this runs on the new filtered list.
        this.syncingFromUrl = true;

        if (!newSlug) {
          // No tab param → try to restore last tab for this group, else 0
          const rememberedSlug = this.lastTabByGroup[this.activeGroup];
          const rememberedIdx = rememberedSlug
            ? this.filteredTabs.findIndex(t => t.slug === rememberedSlug)
            : -1;
          this.activeTabIndex = rememberedIdx !== -1 ? rememberedIdx : 0;
        } else {
          const idx = this.filteredTabs.findIndex(t => t.slug === newSlug);
          this.activeTabIndex = idx !== -1 ? idx : 0;
        }

        this.$nextTick(() => { this.syncingFromUrl = false; });
      }
    }
  },

  methods: {
    selectTab(index) {
      this.activeTabIndex = index;
      this.drawerOpen = false;
      // The activeTabIndex watcher handles the router push
    },

    // Used by both desktop group pills and mobile group buttons
    selectGroup(groupKey) {
      this.drawerOpen = false;

      if (groupKey === this.activeGroup) return;

      this.syncingFromUrl = true;
      this.activeGroup = groupKey;

      // Restore last tab in this group, else first
      const rememberedSlug = this.lastTabByGroup[groupKey];
      const rememberedIdx = rememberedSlug
        ? this.filteredTabs.findIndex(t => t.slug === rememberedSlug)
        : -1;
      this.activeTabIndex = rememberedIdx !== -1 ? rememberedIdx : 0;

      const firstTab = this.filteredTabs[this.activeTabIndex];
      const query = firstTab
        ? { group: groupKey, tab: firstTab.slug }
        : { group: groupKey };

      // Remember the tab we're landing on
      if (firstTab) {
        this.lastTabByGroup = {
          ...this.lastTabByGroup,
          [groupKey]: firstTab.slug
        };
      }

      this.$router
        .push({ path: "/admin_tools/main/", query })
        .catch(err => {
          if (err && err.name !== "NavigationDuplicated") {
            console.error("Group navigation error:", err);
          }
        })
        .finally(() => {
          this.$nextTick(() => { this.syncingFromUrl = false; });
        });
    },

    onGroupTabInput(index) {
      const g = this.availableGroups[index];
      if (g) this.selectGroup(g.key);
    },

    handleResize() {
      this.isMobile = window.innerWidth < 768;
    },

    loadUser() {
      try {
        this.packet = JSON.parse(localStorage.packet || "{}");
        this.packet.userDetails = JSON.parse(localStorage.userDetails || "{}");
      } catch (err) {
        console.error("Error loading user:", err);
      }
    }
  },

  created() {
    this.loadUser();
  },

  mounted() {
    this.handleResize();
    window.addEventListener("resize", this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  }
};
</script>

<style scoped>
@media (max-width: 767px) {
  .b-tabs {
    display: none;
  }
}

/* Give the group pills (Process Sales / Delivered Orders) breathing room */
.group-tabs-wrapper {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 1rem;
  border-radius: 0.25rem;
}

.group-tabs-wrapper ::v-deep .nav-pills .nav-link {
  padding: 0.6rem 1.5rem;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.group-tabs-wrapper ::v-deep .nav-pills .nav-link.active {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/AdminToolsMain.vue